export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Same key the mockups use (`sg-theme`). */
export const THEME_STORAGE_KEY = "sg-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
const PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

export function readStoredPreference(): ThemePreference | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.find((p) => p === stored) ?? null;
  } catch {
    return null;
  }
}

export function storePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage unavailable (private mode / blocked): the theme still applies for this page view.
  }
}

export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") return systemPrefersDark() ? "dark" : "light";
  return preference;
}

export function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

/** Call before the first render to avoid a flash of the wrong theme. */
export function applyStoredTheme(fallback: ThemePreference = "light"): ResolvedTheme {
  const resolved = resolveTheme(readStoredPreference() ?? fallback);
  applyTheme(resolved);
  return resolved;
}

/** useSyncExternalStore-compatible subscription to the OS colour-scheme preference. */
export function subscribeSystemTheme(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") return () => undefined;
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}
